import { useState, useRef, useEffect } from 'react';
import type { AppNotification } from '../hooks/useAnalysisJobs';

interface Props {
  notifications: AppNotification[];
  unreadCount: number;
  runningCount: number;
  queuedCount: number;
  onOpen: (notif: AppNotification) => void;
  onMarkAllRead: () => void;
  onDismiss: (id: string) => void;
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "à l'instant";
  if (min < 60) return `il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `il y a ${h} h`;
  return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

export default function NotificationBell({
  notifications,
  unreadCount,
  runningCount,
  queuedCount,
  onOpen,
  onMarkAllRead,
  onDismiss,
}: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const activeCount = runningCount + queuedCount;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative grid h-9 w-9 place-items-center rounded-full bg-ink text-white transition-transform hover:scale-105 active:scale-95"
        aria-label="Notifications"
        title="Notifications"
      >
        <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
          <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
          <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 grid place-items-center rounded-full bg-rose-pastel-400 text-ink text-[10px] font-bold leading-none ring-2 ring-cream-app">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-1rem)] panel-ink rounded-2xl border border-white/10 z-[70] overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/10">
            <span className="text-sm font-semibold text-white">Notifications</span>
            {unreadCount > 0 && (
              <button onClick={onMarkAllRead} className="text-xs text-rose-pastel-400 hover:underline">
                Tout marquer comme lu
              </button>
            )}
          </div>

          {activeCount > 0 && (
            <div className="px-4 py-2 text-xs text-white/55 bg-white/5 border-b border-white/10 flex items-center gap-2">
              <svg className="w-3.5 h-3.5 animate-spin text-rose-pastel-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6}>
                <path strokeLinecap="round" d="M12 3a9 9 0 0 1 9 9" />
              </svg>
              {runningCount > 0 && `${runningCount} analyse${runningCount > 1 ? 's' : ''} en cours`}
              {runningCount > 0 && queuedCount > 0 && ' · '}
              {queuedCount > 0 && `${queuedCount} en attente`}
            </div>
          )}

          <div className="max-h-80 overflow-y-auto scrollbar-visaly scrollbar-ink">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-white/40">
                Aucune notification pour l'instant.
              </div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n.id}
                  className={`group relative flex items-start gap-3 px-4 py-3 border-b border-white/8 cursor-pointer transition-colors hover:bg-white/6 ${
                    !n.read ? 'bg-white/8' : ''
                  }`}
                  onClick={() => {
                    onOpen(n);
                    setOpen(false);
                  }}
                >
                  <span
                    className={`mt-1 shrink-0 w-2 h-2 rounded-full ${
                      n.read ? 'bg-transparent' : n.kind === 'analysis_error' ? 'bg-red-400' : 'bg-rose-pastel-400'
                    }`}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-white/80 leading-snug line-clamp-2">{n.message}</p>
                    <p className="text-[11px] text-white/40 mt-0.5">{timeAgo(n.createdAt)}</p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDismiss(n.id);
                    }}
                    className="shrink-0 p-1 rounded text-white/30 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Ignorer"
                  >
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
